import { Injectable } from '@angular/core';
import { AlertProblemSort, NotificationType } from '../../enums/generated.enums';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { DateHelper } from '../../helpers/date.helper';
import {
  AlertHistogram,
  AlertMuteModel,
  AlertMuteRequest,
  AlertOccurrencesResult,
  AlertProblem,
  AlertProblemsResult,
  AlertsPage,
  AlertsSummary,
  AttentionCard,
  RecentAction,
} from '../../types/generated.types';
import { BaseApiService } from './base-api.service';

/** What the page is looking at; mirrors the server's AlertsFilter and is sent as query parameters. */
export interface AlertsQuery {
  from?: Date;
  to?: Date;
  types?: NotificationType[];
  processIds?: string[];
  resourceIds?: string[];
  reasons?: string[];
  tenantIds?: string[];
  search?: string;
  spanFrom?: Date;
  spanTo?: Date;
  muted?: boolean;
  includeCovered?: boolean;
  sort?: AlertProblemSort;
  sortDescending?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AlertsApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/alerts`;
  }

  async getSummary(tenantId: string, query: AlertsQuery): Promise<AlertsSummary> {
    const summary = await this.get<AlertsSummary>(`summary${this.getTenantParameter(tenantId)}`, this.toParams(query));
    convertSummary(summary);
    return summary;
  }

  /** The summary and the first problems in one request - the page's first load reads the window once. */
  async getPage(tenantId: string, query: AlertsQuery, pageSize: number): Promise<AlertsPage> {
    const page = await this.get<AlertsPage>(`page${this.getTenantParameter(tenantId)}`, { ...this.toParams(query), pageSize, pageIndex: 0 });
    convertSummary(page.summary);
    page.problems?.problems?.forEach(convertProblem);
    return page;
  }

  async getProblems(tenantId: string, query: AlertsQuery, pageSize: number, pageIndex: number): Promise<AlertProblemsResult> {
    const result = await this.get<AlertProblemsResult>(`problems${this.getTenantParameter(tenantId)}`, { ...this.toParams(query), pageSize, pageIndex });
    result.problems?.forEach(convertProblem);
    return result;
  }

  async getOccurrences(tenantId: string, problemId: string, query: AlertsQuery): Promise<AlertOccurrencesResult> {
    const result = await this.get<AlertOccurrencesResult>(`problems/${problemId}/alerts${this.getTenantParameter(tenantId)}`, this.toParams({ from: query.from, to: query.to }));
    result.occurrences?.forEach(o => convertDates(o, ['timestamp']));
    convertHistogram(result.histogram);
    return result;
  }

  async getActiveProblemsCount(tenantId: string): Promise<number> {
    return await this.get<number>(`count${this.getTenantParameter(tenantId)}`);
  }

  async getMutes(tenantId: string): Promise<AlertMuteModel[]> {
    const mutes = await this.get<AlertMuteModel[]>(`mutes${this.getTenantParameter(tenantId)}`);
    mutes.forEach(convertMute);
    return mutes;
  }

  async upsertMute(request: AlertMuteRequest): Promise<AlertMuteModel> {
    const mute = await this.postWithResult<unknown, AlertMuteModel>('mutes', { ...request, until: DateHelper.formatApiDateTime(request.until) });
    convertMute(mute);
    return mute;
  }

  async deleteMute(id: string): Promise<void> {
    await this.delete(`mutes/${id}`);
  }

  private toParams(query: AlertsQuery): Record<string, unknown> {
    return {
      from: query.from ? DateHelper.formatApiDateTime(query.from) : undefined,
      to: query.to ? DateHelper.formatApiDateTime(query.to) : undefined,
      types: query.types,
      processIds: query.processIds,
      resourceIds: query.resourceIds,
      reasons: query.reasons,
      tenantIds: query.tenantIds,
      search: query.search ? query.search : undefined,
      spanFrom: query.spanFrom ? DateHelper.formatApiDateTime(query.spanFrom) : undefined,
      spanTo: query.spanTo ? DateHelper.formatApiDateTime(query.spanTo) : undefined,
      muted: query.muted ? true : undefined,
      includeCovered: query.includeCovered ? true : undefined,
      sort: query.sort,
      sortDescending: query.sortDescending,
    };
  }
}

function convertDates<T>(target: T, fields: (keyof T)[]): void {
  if (target == null) {
    return;
  }
  fields.forEach(field => {
    const value = target[field] as unknown;
    if (typeof value === 'string') {
      (target as Record<keyof T, unknown>)[field] = DateHelper.parseApiDate(value);
    }
  });
}

function convertSummary(summary: AlertsSummary): void {
  if (summary == null) {
    return;
  }
  convertDates(summary, ['from', 'to']);
  convertHistogram(summary.histogram);
  summary.cards?.forEach(convertCard);
  summary.muteRules?.forEach(convertMute);
}

function convertHistogram(histogram: AlertHistogram): void {
  if (histogram == null) {
    return;
  }
  convertDates(histogram, ['from', 'to']);
  ApiConversionHelper.convertTimeSpans(histogram, ['bucket']);
  histogram.buckets?.forEach(b => convertDates(b, ['start']));
}

function convertActions(actions: RecentAction[]): void {
  actions?.forEach(a => convertDates(a, ['timestamp']));
}

function convertProblem(problem: AlertProblem): void {
  convertDates(problem, ['first', 'last', 'endOfQuarantine']);
  ApiConversionHelper.convertTimeSpans(problem, ['cadence', 'recurrence']);
  convertActions(problem.recentActions);
  convertMute(problem.mute);
}

function convertCard(card: AttentionCard): void {
  convertDates(card, ['first', 'last', 'endOfQuarantine']);
  ApiConversionHelper.convertTimeSpans(card, ['cadence']);
  convertActions(card.recentActions);
}

function convertMute(mute: AlertMuteModel): void {
  convertDates(mute, ['until', 'created', 'modified']);
}
